/**
 * UserFiltersContext
 *
 * Provides a single source of truth for the two global dashboard filters:
 *   1. Sport filter  — based on the user's registered sports (My Sports)
 *   2. Location filter — layered: GPS → profile home → manual override
 *
 * Usage:
 *   const { filterParams, sportParams, location, radius, ... } = useUserFilters();
 *   const data = await matchService.getAllMatches({ ...filterParams, ...otherParams });
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { getUserSports } from '../services/api/sportsService';
import apiService from '../services/api/apiService';
import AuthContext from './AuthContext';

// ── Context ───────────────────────────────────────────────────────────────────

/** The radius choices (km), smallest first. Auto-expand steps through them. */
export const RADIUS_RINGS = [5, 20, 50, 100];
export const DEFAULT_RADIUS_KM = 50;

/** How long pages wait for the first location before showing the prompt. */
const LOCATION_WAIT_MS = 12000;

/** The next ring above `km`, never past the largest. */
export function nextRadiusRing(km) {
  return RADIUS_RINGS.find((ring) => ring > km) ?? RADIUS_RINGS[RADIUS_RINGS.length - 1];
}

/** Browser position as { lat, lng }; rejects when unsupported or denied. */
function readGpsPosition() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { timeout: 10000, maximumAge: 60000 }
    );
  });
}

/** A "City, Region" label for a point; null when it can't be looked up. */
async function reverseGeocodeLabel(lat, lng) {
  const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
  if (!apiKey) return null;
  try {
    const r = await fetch(
      `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&apiKey=${apiKey}`
    );
    const p = (await r.json())?.features?.[0]?.properties;
    if (!p) return null;
    return [p.city || p.county || p.name, p.state].filter(Boolean).join(', ') || null;
  } catch {
    return null;
  }
}

/**
 * Saves a location as the user's home city when they don't have one yet, so
 * the next visit (and other devices) start from it without asking again.
 */
function saveAsHomeLocation({ lat, lng, label }) {
  return Promise.resolve()
    .then(() => apiService.updateUserProfile({
      home_lat: lat, home_lng: lng, home_city: (label || '').slice(0, 100) || null,
    }))
    .catch(() => {});
}

export const UserFiltersContext = createContext(null);

// ── Haversine distance (km) ───────────────────────────────────────────────────

/**
 * Calculates the great-circle distance between two lat/lng points (in km).
 * Used on the frontend to display "X km away" without a backend round-trip.
 */
export function getDistanceKm(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function UserFiltersProvider({ children }) {
  /** All sports registered by the user, shape: [{ id, name, skill_level, ... }] */
  const [userSports, setUserSports] = useState([]);

  /**
   * IDs of user's sports currently selected as a filter.
   * Defaults to ALL user sports (show everything the user plays).
   * User can narrow by toggling individual pills in the FilterBar.
   */
  const [selectedSportIds, setSelectedSportIds] = useState([]);

  /**
   * Active location, one of:
   *  { lat, lng, label: string, source: 'gps' | 'profile' | 'manual' }
   *  null = no location set, skip proximity filter
   */
  const [location, setLocation] = useState(null);

  /** Search radius in km (used in UI; converted to metres for API). */
  const [radius, setRadiusKm] = useState(DEFAULT_RADIUS_KM);

  /** True while the signed-in user has a saved home location in their profile. */
  const [hasHomeLocation, setHasHomeLocation] = useState(false);

  /** Set to a larger value when the auto-expand kicks in (no results at `radius`). */
  const [expandedRadius, setExpandedRadius] = useState(null);

  /** Human-readable message shown under FilterBar when radius was auto-expanded. */
  const [radiusExpansionMsg, setRadiusExpansionMsg] = useState('');

  /** True once the user picks a ring themselves; auto-expand then leaves it alone. */
  const [radiusPinned, setRadiusPinned] = useState(false);

  /**
   * True once the initial getUserSports() call has settled (success or failure).
   * Pages should wait for this before making their first sport-filtered API call
   * to avoid an unfiltered "flash" on first render.
   */
  const [isSportsReady, setIsSportsReady]       = useState(false);
  const [isLocationReady, setIsLocationReady]   = useState(false);
  /**
   * isFiltersReady = true only after BOTH sports AND the profile-home-location
   * fetch have settled. This ensures the very first API call on every list page
   * (Teams, Leagues, Tournaments, Drop-ins, Matches) already carries the home
   * city lat/lng/radius — no unfiltered "show everything" flash on load.
   */
  const isFiltersReady = isSportsReady && isLocationReady;

  // ── Load user sports once there is a session ──────────────────────────────
  // Inside an AuthProvider, wait for auth to settle and only call the
  // authenticated endpoints when someone is signed in (no 401 noise on the
  // login page). Without an AuthProvider (isolated tests) load on mount.
  const auth = useContext(AuthContext);
  const hasAuthProvider = auth != null;
  const authLoading = hasAuthProvider ? Boolean(auth.isLoading) : false;
  const sessionKey = hasAuthProvider
    ? (auth.isAuthenticated ? String(auth.user?.id ?? 'session') : null)
    : 'no-auth-provider';

  useEffect(() => {
    if (authLoading) return undefined;

    if (sessionKey == null) {
      // Signed out: clear user-specific filters and don't block pages
      setHasHomeLocation(false);
      setUserSports([]);
      setSelectedSportIds([]);
      setLocation((prev) => (prev?.source === 'profile' ? null : prev));
      setIsSportsReady(true);
      setIsLocationReady(true);
      return undefined;
    }

    let cancelled = false;
    setIsSportsReady(false);
    setIsLocationReady(false);

    getUserSports()
      .then((res) => {
        if (cancelled) return;
        const sports =
          res?.data?.sports || res?.sports || res?.data || [];
        const normalized = Array.isArray(sports) ? sports : [];
        setUserSports(normalized);
        // Default: all user sports selected
        setSelectedSportIds(normalized.map((s) => s.sport_id || s.id));
      })
      .catch(() => {
        // Not critical — fall back to no sport filter (show all)
      })
      .finally(() => {
        if (!cancelled) setIsSportsReady(true);
      });

    // Every list is local: start from the profile home location, and when
    // there is none, ask the browser for the current position once and save
    // it as home. Without either, pages show "set your location" instead of
    // everything everywhere (needsLocation).
    const resolveLocation = async () => {
      let home = null;
      try {
        const profile = await apiService.getUserProfile();
        const homeLat = parseFloat(profile?.home_lat);
        const homeLng = parseFloat(profile?.home_lng);
        if (Number.isFinite(homeLat) && Number.isFinite(homeLng)) {
          home = { lat: homeLat, lng: homeLng, label: profile.home_city || 'Home City', source: 'profile' };
        }
      } catch {
        // Fall through to GPS
      }
      if (cancelled) return;
      setHasHomeLocation(home != null);
      if (home) {
        // Only apply profile home if no GPS or manual location is already active
        setLocation((prev) => prev ?? home);
        return;
      }
      try {
        const pos = await readGpsPosition();
        if (cancelled) return;
        const gps = { ...pos, label: 'Current Location', source: 'gps' };
        setLocation((prev) => prev ?? gps);
        const label = await reverseGeocodeLabel(pos.lat, pos.lng);
        if (cancelled) return;
        if (label) {
          setLocation((prev) => (prev?.source === 'gps' && prev.lat === pos.lat ? { ...prev, label } : prev));
        }
        await saveAsHomeLocation({ ...pos, label: label || 'Current Location' });
        if (!cancelled) setHasHomeLocation(true);
      } catch {
        // Denied or unsupported: the FilterBar asks the user to pick a city
      }
    };

    // Pages wait for this so their first call already carries the location.
    // An unanswered permission prompt never times out on its own, so stop
    // waiting after a while; a later answer still sets the location.
    const markReady = () => { if (!cancelled) setIsLocationReady(true); };
    const readyTimer = setTimeout(markReady, LOCATION_WAIT_MS);
    resolveLocation().finally(() => {
      clearTimeout(readyTimer);
      markReady();
    });

    return () => {
      cancelled = true;
      clearTimeout(readyTimer);
    };
  }, [authLoading, sessionKey]);

  // ── Location helpers ───────────────────────────────────────────────────────

  /**
   * Requests browser geolocation.
   * Returns a Promise<{ lat, lng }> — resolves on success, rejects on denial.
   */
  const requestGpsLocation = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const loc = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              label: 'Current Location',
              source: 'gps',
            };
            setLocation(loc);
            setExpandedRadius(null);
            setRadiusExpansionMsg('');
            if (sessionKey != null && !hasHomeLocation) {
              saveAsHomeLocation(loc).then(() => setHasHomeLocation(true));
            }
            resolve(loc);
          },
          (err) => reject(err),
          { timeout: 10000, maximumAge: 60000 }
        );
      }),
    [sessionKey, hasHomeLocation]
  );

  /**
   * Sets a manual location override (session-only — does not change the profile).
   */
  const setManualLocation = useCallback((lat, lng, label) => {
    const loc = { lat: parseFloat(lat), lng: parseFloat(lng), label, source: 'manual' };
    setLocation(loc);
    setExpandedRadius(null);
    setRadiusExpansionMsg('');
    // The first city someone picks becomes their home when they have none
    if (sessionKey != null && !hasHomeLocation) {
      saveAsHomeLocation(loc).then(() => setHasHomeLocation(true));
    }
  }, [sessionKey, hasHomeLocation]);

  /** Picks a radius ring; a manual choice replaces any auto-expansion. */
  const setRadius = useCallback((km) => {
    setRadiusKm(km);
    setRadiusPinned(true);
    setExpandedRadius(null);
    setRadiusExpansionMsg('');
  }, []);

  // ── Sport toggle ───────────────────────────────────────────────────────────

  const toggleSport = useCallback((sportId) => {
    setSelectedSportIds((prev) => {
      const id = Number(sportId);
      // Don't allow deselecting the last sport (would show nothing)
      if (prev.includes(id) && prev.length === 1) return prev;
      return prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
    });
  }, []);

  /** Selects every user sport (resets to default). */
  const selectAllSports = useCallback(() => {
    setSelectedSportIds(userSports.map((s) => s.sport_id || s.id));
  }, [userSports]);

  // ── Derived filter params ready to spread into API calls ──────────────────

  /**
   * For entities with integer sport_id FK (matches, tournaments, drop-ins).
   * Always filters to the user's selected sports so the user only sees content
   * for sports they play. The backend receives sport_ids even when all user
   * sports are selected — this limits results to the user's sport set.
   */
  const filterParams = useMemo(() => {
    const params = {};

    // Always include sport filter when the user has registered sports.
    // An empty selection is treated as "all" to avoid showing nothing.
    if (selectedSportIds.length > 0) {
      params.sport_ids = selectedSportIds.join(',');
    }

    if (location && Number.isFinite(location.lat) && Number.isFinite(location.lng) && (expandedRadius || radius) > 0) {
      params.lat = location.lat;
      params.lng = location.lng;
      params.radius = (expandedRadius || radius) * 1000; // km → metres
    }

    return params;
  }, [selectedSportIds, location, radius, expandedRadius]);

  /**
   * For entities that store sport as a text name (leagues, teams use sport_type VARCHAR).
   * Always returns the selected sport names when the user has registered sports.
   */
  const sportTypeParams = useMemo(() => {
    if (selectedSportIds.length === 0) return {};

    const selectedNames = userSports
      .filter((s) => selectedSportIds.includes(s.sport_id || s.id))
      .map((s) => s.name);

    return selectedNames.length > 0
      ? { sport_types: selectedNames.join(',') }
      : {};
  }, [userSports, selectedSportIds]);

  // ── Auto-expand helper ────────────────────────────────────────────────────

  /**
   * Called by list pages when a proximity-filtered query returns fewer than
   * `minResults` records. Automatically expands the radius and shows a message.
   */
  const autoExpandRadius = useCallback(
    (currentResultCount, minResults = 5) => {
      // A ring the user picked stays picked, even with few results
      if (!location || radiusPinned) return false;
      // Enough results: keep the current ring. Dropping back to the default
      // here would refetch, find too few again and widen again, in a loop.
      if (currentResultCount >= minResults) return false;
      // Step out one ring at a time (5 → 20 → 50 → 100 km), never past 100
      const current = expandedRadius || radius;
      const capped = nextRadiusRing(current);
      if (capped === current) return false;
      setExpandedRadius(capped);
      // It also widens when a few results came back, so only say "No results" for none
      const found = currentResultCount === 0
        ? 'No results'
        : `Only ${currentResultCount} result${currentResultCount === 1 ? '' : 's'}`;
      setRadiusExpansionMsg(
        `${found} within ${current} km — showing results within ${capped} km`
      );
      return true;
    },
    [location, radius, expandedRadius, radiusPinned]
  );

  /**
   * Signed in, filters settled, and still no location (no home saved, GPS
   * denied): list pages show a "set your location" prompt rather than data
   * from every region.
   */
  const needsLocation = isFiltersReady && sessionKey != null && filterParams.lat == null;

  return (
    <UserFiltersContext.Provider
      value={{
        // State
        userSports,
        selectedSportIds,
        location,
        radius,
        expandedRadius,
        radiusExpansionMsg,
        // Actions
        setRadius,
        toggleSport,
        selectAllSports,
        requestGpsLocation,
        setManualLocation,
        autoExpandRadius,
        setRadiusExpansionMsg,
        // Computed params (spread directly into API calls)
        filterParams,
        sportTypeParams,
        // Ready flag: true once the initial sports fetch has settled
        isFiltersReady,
        needsLocation,
      }}
    >
      {children}
    </UserFiltersContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useUserFilters() {
  const ctx = useContext(UserFiltersContext);
  if (!ctx) {
    throw new Error('useUserFilters must be used inside <UserFiltersProvider>');
  }
  return ctx;
}
